import moment from "moment";

type TimeInput = string | Date | number;

export const toLocalTime = (
  utc: TimeInput,
  options: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' }
): string => {
  let date: Date;
  
  if (typeof utc === 'string') {
    const utcString = utc.includes('T') && !utc.endsWith('Z') ? utc + 'Z' : utc;
    date = new Date(utcString);
  } else {
    date = new Date(utc);
  }
  
  return isNaN(date.getTime()) ? 'Invalid date' : 
    new Intl.DateTimeFormat(undefined, options).format(date);
};
export const toRelativeTime = (utc: TimeInput): string => {
  let date: Date;
  
  if (typeof utc === 'string') {
    // Handle your backend format: "2025-07-18T08:13:58.420078" (UTC without Z)
    const utcString = utc.includes('T') && !utc.endsWith('Z') ? utc + 'Z' : utc;
    date = new Date(utcString);
  } else {
    date = new Date(utc);
  }
  
  if (isNaN(date.getTime())) return 'Invalid date';
  
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 2592000) return `${Math.floor(seconds / 86400)}d ago`;
  
  // For older dates, show formatted date
  return toLocalTime(date, { 
    month: 'short', 
    day: 'numeric', 
    ...(seconds > 31536000 && { year: 'numeric' }) 
  });
};

/**
 * Smart time formatter - shows relative time for recent dates, formatted time for older
 */
export const smartTime = (utc: TimeInput, threshold = 86400): string => {
  let date: Date;
  
  if (typeof utc === 'string') {
    // Handle your backend format: "2025-07-18T08:13:58.420078" (UTC without Z)
    const utcString = utc.includes('T') && !utc.endsWith('Z') ? utc + 'Z' : utc;
    date = new Date(utcString);
  } else {
    date = new Date(utc);
  }
  
  if (isNaN(date.getTime())) return 'Invalid date';
  
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  
  return seconds < threshold ? 
    toRelativeTime(utc) : 
    toLocalTime(utc, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/**
 * Preset formatters for common use cases
 */
export const timePresets = {
  time: (utc: TimeInput) => toLocalTime(utc),
  dateTime: (utc: TimeInput) => toLocalTime(utc, { 
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
  }),
  fullDate: (utc: TimeInput) => toLocalTime(utc, { 
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' 
  }),
  relative: toRelativeTime,
  smart: smartTime
};

export const formatDateRange = (dateString?: string) => {
    if(!dateString) return "";
    if (!dateString || dateString === "-") return dateString;

    const formatSingleDate = (dateStr: string) => {
      // Try parsing with specific formats in order of preference
      const formats = [
        'YYYY-MM-DDTHH:mm:ss',
        'YYYY-MM-DD',
        'DD/MM/YYYY',
        'MM/DD/YYYY'
      ];

      for (const format of formats) {
        const parsed = moment(dateStr.trim(), format, true); // strict parsing
        if (parsed.isValid()) {
          return parsed.format('MM/DD/YYYY');
        }
      }

      // Fallback to default parsing if none of the specific formats work
      const fallback = moment(dateStr.trim());
      return fallback.isValid() ? fallback.format('MM/DD/YYYY') : dateStr;
    };

    // Check if it's a date range (contains " - ")
    if (dateString.includes(" - ")) {
      const [startDate, endDate] = dateString.split(" - ");
      const formattedStart = formatSingleDate(startDate);
      const formattedEnd = formatSingleDate(endDate);
      return `${formattedStart} - ${formattedEnd}`;
    } else {
      // Single date
      return formatSingleDate(dateString);
    }
  };